<template>
  <div class="content">
    <div class="sd-pathway-detail-wrap">
      <card class="card no-border-card" no-body>
        <div class="sdd-detail">

          <div v-if="loading" class="sdd-loading">
            <span class="sdd-spinner" aria-label="Loading pathway"></span>
          </div>

          <template v-else-if="pathway">
            <nav class="sdd-breadcrumb">
              <router-link :to="backTo.path" class="sdd-breadcrumb__link">← {{ backTo.label }}</router-link>
            </nav>

            <!-- Pathway header ─────────────────────────────────── -->
            <header class="sdd-header">
              <div class="sdd-header__text">
                <span v-if="pathway.classification" class="sdd-eyebrow">{{ classificationLabel(pathway.classification) }}</span>
                <h1 class="sdd-header__title">{{ pathway.title }}</h1>
                <p v-if="pathway.subtitle" class="sdd-header__sub">{{ pathway.subtitle }}</p>

                <div class="sdd-header__meta">
                  <span class="sdd-status-pill" :class="statusClass(pathway.pathway_status)">
                    {{ statusLabel(pathway.pathway_status) }}
                  </span>
                  <span class="sdd-header__count">{{ sortedModules.length }} modules</span>
                </div>

                <button
                  v-if="pathway.pathway_status === 'not_enrolled'"
                  class="sdd-btn sdd-btn--primary"
                  :disabled="enrolling"
                  @click="enroll">
                  {{ enrolling ? 'Enrolling…' : 'Enroll in this pathway' }}
                </button>
              </div>

              <div v-if="pathway.pathway_status !== 'not_enrolled'" class="sdd-header__progress">
                <svg class="sdd-circle" viewBox="0 0 80 80">
                  <circle cx="40" cy="40" r="34" fill="none" stroke="#e2e8f0" stroke-width="8" />
                  <circle
                    cx="40" cy="40" r="34"
                    fill="none"
                    stroke="#3e7799"
                    stroke-width="8"
                    stroke-linecap="round"
                    :stroke-dasharray="circleCircumference"
                    :stroke-dashoffset="circleOffset"
                    transform="rotate(-90 40 40)" />
                </svg>
                <span class="sdd-circle__pct">{{ overallPct }}%</span>
              </div>
            </header>

            <p v-if="pathway.description" class="sdd-description">{{ pathway.description }}</p>

            <!-- Module list ────────────────────────────────────── -->
            <section class="sdd-modules">
              <h2 class="sdd-modules__heading">Modules</h2>

              <div v-if="sortedModules.length === 0" class="sdd-empty">
                <p>This pathway has no modules yet.</p>
              </div>

              <div v-else class="sdd-module-list">
                <div
                  v-for="(mod, idx) in sortedModules"
                  :key="mod.id"
                  class="sdd-module"
                  :class="{
                    'sdd-module--completed': mod.module_status === 'completed',
                    'sdd-module--active':    mod.module_status === 'in_progress',
                    'sdd-module--locked':    mod.module_status === 'locked',
                    'sdd-module--retired':   mod.removed_from_pathway,
                  }">

                  <div class="sdd-module__number">
                    <svg v-if="mod.module_status === 'completed'" viewBox="0 0 20 20" fill="currentColor" width="20" height="20" aria-hidden="true">
                      <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clip-rule="evenodd" />
                    </svg>
                    <svg v-else-if="mod.module_status === 'locked'" viewBox="0 0 20 20" fill="currentColor" width="18" height="18" aria-hidden="true">
                      <path fill-rule="evenodd" d="M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z" clip-rule="evenodd" />
                    </svg>
                    <span v-else>{{ idx + 1 }}</span>
                  </div>

                  <div class="sdd-module__content">
                    <h3 class="sdd-module__title">{{ mod.course_name || 'Module ' + (idx + 1) }}</h3>
                    <span class="sdd-module__status">{{ moduleStatusLabel(mod.module_status) }}</span>
                    <!-- Taken out of the pathway after this learner
                         completed it. Theirs to keep; simply not required
                         any more. -->
                    <p v-if="mod.removed_from_pathway" class="sdd-module__lock-hint ignore-translation notranslate" translate="no">
                      No longer required in this pathway.
                    </p>
                    <p v-else-if="mod.module_status === 'locked' && mod.gating_rule === 'unlock_on_date' && mod.unlock_at" class="sdd-module__lock-hint">
                      Unlocks on {{ formatUnlockAt(mod.unlock_at) }}.
                    </p>
                    <p v-else-if="mod.module_status === 'locked'" class="sdd-module__lock-hint">
                      Complete the previous module to unlock.
                    </p>
                  </div>

                  <div class="sdd-module__action">
                    <button
                      v-if="mod.module_status === 'in_progress'"
                      class="sdd-btn sdd-btn--sm sdd-btn--primary"
                      @click="openModule(mod)">
                      Continue
                    </button>
                    <button
                      v-else-if="mod.module_status === 'completed'"
                      class="sdd-btn sdd-btn--sm"
                      @click="openModule(mod)">
                      Review
                    </button>
                  </div>
                </div>
              </div>
            </section>
          </template>

          <div v-else class="sdd-empty">
            <p>Pathway not found.</p>
            <router-link to="/safedig/pathways" class="sdd-btn sdd-btn--sm">Back to pathways</router-link>
          </div>

        </div>
      </card>
    </div>
  </div>
</template>

<script>
import { safeDigClassificationLabel } from '@/utils/safeDigPathwayTemplates';

export default {
  name: 'PathwayDetail',
  data() {
    return {
      loading: true,
      enrolling: false,
      pathway: null,
    };
  },
  computed: {
    /**
     * Where the back link goes. Staff came from the pathway dashboard;
     * everyone else reads their pathways on My Courses & Pathways.
     */
    backTo() {
      const role = (typeof localStorage !== 'undefined') ? localStorage.getItem('hot-user') : '';
      return ['super-admin', 'sub-admin'].indexOf(role) !== -1
        ? { path: '/safedig/pathways', label: 'Pathways' }
        : { path: '/employee_courses', label: 'My Courses & Pathways' };
    },
    sortedModules() {
      if (!this.pathway || !this.pathway.modules) return [];
      return [...this.pathway.modules].sort((a, b) => a.sequence_order - b.sequence_order);
    },
    overallPct() {
      const mods = this.sortedModules;
      if (!mods.length) return 0;
      const done = mods.filter(m => m.module_status === 'completed').length;
      return Math.round((done / mods.length) * 100);
    },
    circleCircumference() { return 2 * Math.PI * 34; },
    circleOffset() { return this.circleCircumference * (1 - this.overallPct / 100); },
  },
  created() {
    this.fetchPathway();
  },
  methods: {
    fetchPathway() {
      this.loading = true;
      this.$http.get('safedig/pathways/' + this.$route.params.id)
        .then(resp => { this.pathway = resp.data; })
        .catch(() => { this.pathway = null; })
        .finally(() => { this.loading = false; });
    },
    enroll() {
      this.enrolling = true;
      this.$http.post('safedig/pathways/' + this.pathway.id + '/enroll')
        .then(() => { this.fetchPathway(); })
        .catch(err => this.flashError(err, 'Could not enroll in this pathway.'))
        .finally(() => { this.enrolling = false; });
    },
    /**
     * Modules are courses, so playing one hands off to Safe Dig's existing
     * course player rather than a pathway-specific viewer. Passing the
     * course there is what completes the module — there is no manual
     * "mark complete"; progress is read back on the next fetch.
     */
    openModule(mod) {
      if (!mod.course_id) {
        this.flash('This module has no course attached yet.', 'warning');
        return;
      }
      this.$router.push({ path: '/course_instructions', query: { id: mod.course_id } });
    },
    flash(msg, kind) {
      if (this.$toast && this.$toast[kind]) this.$toast[kind](msg, { timeout: 4000 });
    },
    flashError(err, fallback) {
      const msg = (err && err.response && err.response.data && err.response.data.message) || fallback;
      this.flash(msg, 'error');
    },
    classificationLabel(c) { return safeDigClassificationLabel(c); },
    statusLabel(s) {
      const map = {
        not_enrolled: 'Not enrolled',
        enrolled: 'Enrolled',
        in_progress: 'In progress',
        completed: 'Completed',
      };
      return map[s] || s;
    },
    statusClass(s) {
      return {
        'sdd-status-pill--neutral': !s || s === 'not_enrolled',
        'sdd-status-pill--info':    s === 'enrolled',
        'sdd-status-pill--active':  s === 'in_progress',
        'sdd-status-pill--success': s === 'completed',
      };
    },
    formatUnlockAt(iso) {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return iso;
      return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
    },
    moduleStatusLabel(s) {
      const map = { locked: 'Locked', in_progress: 'In progress', completed: 'Completed' };
      return map[s] || s;
    },
  },
};
</script>

<style scoped>
.sd-pathway-detail-wrap { width: 100%; }
.sd-pathway-detail-wrap >>> .card.no-border-card {
  border: 0;
  box-shadow: none;
  background: transparent;
}

.sdd-detail {
  --sd-accent: #3e7799;
  --sd-accent-dark: #2d5a73;
  --sd-accent-soft: rgba(62, 119, 153, 0.14);
  --sd-navy: #1e3a5f;
  --sd-text: #0f172a;
  --sd-muted: #475569;
  --sd-border: #e2e8f0;
  --sd-radius: 12px;
  --sd-shadow: 0 1px 2px rgba(15, 23, 42, 0.06), 0 6px 16px rgba(15, 23, 42, 0.05);

  padding: 1.75rem 2rem 2.5rem;
  color: var(--sd-text);
  background: #f8fafc;
  min-height: 100%;
}

.sdd-breadcrumb { margin-bottom: 1rem; }
.sdd-breadcrumb__link {
  font-size: 0.875rem;
  color: var(--sd-accent);
  font-weight: 600;
  text-decoration: none;
}
.sdd-breadcrumb__link:hover { color: var(--sd-accent-dark); text-decoration: underline; }

/* Header ──────────────────────────────────────────────────── */
.sdd-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1.5rem;
  background: #fff;
  border: 1px solid var(--sd-border);
  border-radius: var(--sd-radius);
  box-shadow: var(--sd-shadow);
  padding: 1.5rem;
  flex-wrap: wrap;
}
.sdd-header__text { flex: 1; min-width: 240px; }
.sdd-eyebrow {
  display: inline-block;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--sd-accent);
  margin-bottom: 0.4rem;
}
.sdd-header__title {
  font-size: 1.65rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--sd-navy);
  margin: 0 0 0.35rem;
  line-height: 1.2;
}
.sdd-header__sub { color: var(--sd-muted); font-size: 0.95rem; margin: 0 0 0.75rem; }
.sdd-header__meta {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
}
.sdd-header__count { font-size: 0.85rem; color: var(--sd-muted); }

.sdd-status-pill {
  display: inline-block;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
}
.sdd-status-pill--neutral { background: #f1f5f9; color: var(--sd-muted); }
.sdd-status-pill--info    { background: var(--sd-accent-soft); color: var(--sd-accent); }
.sdd-status-pill--active  { background: #fef3c7; color: #b45309; }
.sdd-status-pill--success { background: #dcfce7; color: #15803d; }

.sdd-header__progress { position: relative; width: 80px; height: 80px; flex-shrink: 0; }
.sdd-circle { width: 80px; height: 80px; }
.sdd-circle__pct {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--sd-navy);
}

.sdd-description {
  margin: 1.25rem 0 0;
  color: var(--sd-muted);
  font-size: 0.9375rem;
  line-height: 1.6;
  max-width: 70ch;
}

/* Buttons ─────────────────────────────────────────────────── */
.sdd-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.6rem 1.05rem;
  border-radius: 10px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid var(--sd-border);
  background: #fff;
  color: var(--sd-text);
  line-height: 1;
  text-decoration: none;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}
.sdd-btn:hover:not(:disabled) { border-color: var(--sd-accent); color: var(--sd-accent); }
.sdd-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.sdd-btn--sm { padding: 0.4rem 0.8rem; font-size: 0.8125rem; }
.sdd-btn--primary {
  background: var(--sd-accent);
  border-color: var(--sd-accent);
  color: #fff;
}
.sdd-btn--primary:hover:not(:disabled) {
  background: var(--sd-accent-dark);
  border-color: var(--sd-accent-dark);
  color: #fff;
}

/* Modules ─────────────────────────────────────────────────── */
.sdd-modules { margin-top: 1.75rem; }
.sdd-modules__heading {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--sd-navy);
  margin: 0 0 0.9rem;
}
.sdd-module-list { display: flex; flex-direction: column; gap: 0.65rem; }
.sdd-module {
  display: flex;
  align-items: center;
  gap: 1rem;
  background: #fff;
  border: 1px solid var(--sd-border);
  border-radius: var(--sd-radius);
  box-shadow: var(--sd-shadow);
  padding: 1rem 1.15rem;
  border-left: 4px solid var(--sd-border);
}
.sdd-module--completed { border-left-color: #15803d; }
.sdd-module--active    { border-left-color: var(--sd-accent); }
.sdd-module--locked    { opacity: 0.72; }

.sdd-module__number {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #f1f5f9;
  color: var(--sd-muted);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  flex-shrink: 0;
}
.sdd-module--completed .sdd-module__number { background: #dcfce7; color: #15803d; }
.sdd-module--active .sdd-module__number { background: var(--sd-accent-soft); color: var(--sd-accent); }

.sdd-module__content { flex: 1; min-width: 0; }
.sdd-module__title {
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--sd-navy);
  margin: 0 0 0.2rem;
}
.sdd-module__status { font-size: 0.8rem; color: var(--sd-muted); }
.sdd-module__lock-hint { font-size: 0.775rem; color: #94a3b8; margin: 0.25rem 0 0; }
/* Completed, then removed from the pathway: kept on the record, marked out
   from what the pathway still asks for. */
.sdd-module--retired { border-style: dashed; }
.sdd-module__action { display: flex; gap: 0.4rem; flex-shrink: 0; flex-wrap: wrap; }

/* Loading / empty ─────────────────────────────────────────── */
.sdd-loading, .sdd-empty {
  padding: 3rem 1rem;
  text-align: center;
  color: var(--sd-muted);
}
.sdd-spinner {
  display: inline-block;
  width: 28px;
  height: 28px;
  border: 3px solid var(--sd-border);
  border-top-color: var(--sd-accent);
  border-radius: 50%;
  animation: sdd-spin 0.8s linear infinite;
}
@keyframes sdd-spin { to { transform: rotate(360deg); } }

@media (max-width: 640px) {
  .sdd-detail { padding: 1.25rem 1rem 2rem; }
  .sdd-module { flex-wrap: wrap; }
}
</style>
